import { useState, useEffect } from 'react';
import axios from 'axios';
import { PasswordRules } from '../components/auth/PasswordRules';
import { isStrongPassword, PASSWORD_RULE_MSG } from '../lib/passwordPolicy';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Shield, Key, Check, X, Eye, EyeOff, MapPin } from 'lucide-react';
import { ROLE_LABELS } from '../lib/roleBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


import { useEmailCheck, EmailCheckHint } from '../components/EmailCheckHint';
import { PermissionsEditor } from '../components/users/PermissionsEditor';
import { UserFilters } from '../components/users/UserFilters';
import { StorePagination } from '../components/stores/StorePagination';

// Mağaza firmasına bağlı firma kullanıcısı "Mağaza/Kiracı Yetkilisi" olarak gösterilir
const roleKey = (u) => (u.role === 'firma_yetkilisi' && u.company_type === 'magaza' ? 'magaza_yetkilisi' : u.role);

const UsersPage = () => {
  const { user: currentUser, canCreate, canEdit, canDelete, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all');
  const [q, setQ] = useState('');
  const [locations, setLocations] = useState([]);
  const [predefinedRoles, setPredefinedRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
    is_active: true,
    permissions: {},
    allowed_locations: []
  });
  
  const [newPassword, setNewPassword] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [reload, setReload] = useState(0);
  const fetchData = () => setReload(r => r + 1);

  useEffect(() => {
    Promise.all([axios.get(`${API}/locations`), axios.get(`${API}/roles/predefined`)])
      .then(([l, r]) => { setLocations(l.data); setPredefinedRoles(r.data); })
      .catch(e => console.error('Veri yüklenemedi:', e));
  }, []);

  // Liste: rol + arama + sayfalama sunucuda (aramada kısa gecikme)
  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/users`, { params: { page, page_size: pageSize, q: q.trim(), role: roleFilter } })
        .then(r => { setUsers(r.data.items); setTotal(r.data.total); setCounts(r.data.counts); })
        .catch(e => console.error('Kullanıcılar yüklenemedi:', e))
        .finally(() => setLoading(false));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [page, pageSize, q, roleFilter, reload]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        name: user.name,
        role: user.role || 'user',
        is_active: user.is_active !== false,
        permissions: user.permissions || {},
        allowed_locations: user.allowed_locations || []
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        name: '',
        role: 'user',
        is_active: true,
        permissions: predefinedRoles.viewer?.permissions || {},
        allowed_locations: []
      });
    }
    setDialogOpen(true);
  };

  const handleRoleChange = (role) => {
    const roleData = predefinedRoles[role];
    setFormData({
      ...formData,
      role,
      permissions: roleData?.permissions || formData.permissions
    });
  };

  const handlePermissionChange = (module, action, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [action]: checked
        }
      }
    }));
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const currentLocations = prev.allowed_locations || [];
      return {
        ...prev,
        allowed_locations: currentLocations.includes(locationId)
          ? currentLocations.filter(id => id !== locationId)
          : [...currentLocations, locationId]
      };
    });
  };

  const handleSelectAllLocations = () => {
    const currentLocations = formData.allowed_locations || [];
    if (currentLocations.length === locations.length) {
      setFormData(prev => ({ ...prev, allowed_locations: [] }));
    } else {
      setFormData(prev => ({ ...prev, allowed_locations: locations.map(l => l.id) }));
    }
  };

  const userEmailCheck = useEmailCheck(dialogOpen && !editingUser ? formData.email : '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Güncelleme
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: formData.name,
          role: formData.role,
          is_active: formData.is_active,
          permissions: formData.permissions,
          allowed_locations: formData.allowed_locations || []
        });
      } else {
        // Yeni kullanıcı - tüm bilgileri tek seferde gönder
        if (!formData.password) {
          toast.error('Şifre zorunludur');
          return;
        }
        if (!isStrongPassword(formData.password)) {
          toast.error(PASSWORD_RULE_MSG);
          return;
        }
        if (!formData.email) {
          toast.error('E-posta zorunludur');
          return;
        }
        await axios.post(`${API}/users`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          role: formData.role,
          permissions: formData.permissions,
          allowed_locations: formData.allowed_locations || []
        });
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('İşlem başarısız:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      }
    }
  };

  const handleResetPassword = async () => {
    if (!isStrongPassword(newPassword)) {
      toast.error(PASSWORD_RULE_MSG);
      return;
    }
    try {
      await axios.put(`${API}/users/${selectedUserForPassword.id}/reset-password`, {
        new_password: newPassword
      });
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUserForPassword(null);
    } catch (error) {
      console.error('Şifre sıfırlama başarısız:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUserForDelete.id}`);
      setDeleteDialogOpen(false);
      setSelectedUserForDelete(null);
      fetchData();
    } catch (error) {
      console.error('Silme başarısız:', error);
    }
  };

  const shown = users;

  const toggleFirmAccess = async (u, enabled) => {
    try {
      await axios.put(`${API}/firm-access/staff/${u.id}`, { enabled });
      toast.success(`${u.name}: Firma Erişimi ${enabled ? 'açıldı' : 'kapatıldı'}`);
      fetchData();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'İşlem başarısız');
    }
  };

  const getRoleBadge = (u) => {
    const roleInfo = ROLE_LABELS[roleKey(u)] || ROLE_LABELS.user;
    return <Badge className={roleInfo.color} data-testid={`user-role-${u.id}`}>{roleInfo.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kullanıcı Yönetimi</h1>
          <p className="text-slate-500 mt-1">Sistem kullanıcılarını ve yetkilerini yönetin</p>
        </div>
        {canCreate('users') && (
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-user-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Kullanıcı Ekle
          </Button>
        )}
      </div>

      <UserFilters counts={counts} labels={ROLE_LABELS} role={roleFilter} onRole={(r) => { setRoleFilter(r); setPage(1); }} q={q} onQ={(v) => { setQ(v); setPage(1); }} />

      {/* Users Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Kullanıcı</TableHead>
                <TableHead className="font-semibold">E-posta</TableHead>
                <TableHead className="font-semibold">Rol</TableHead>
                <TableHead className="font-semibold">Firma / Kiracı</TableHead>
                <TableHead className="font-semibold">Lokasyonlar</TableHead>
                <TableHead className="font-semibold">Durum</TableHead>
                <TableHead className="font-semibold text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="h-8 w-8 text-slate-300" />
                      <p className="text-slate-500" data-testid="users-empty">{counts.all ? 'Filtreye uyan kullanıcı yok' : 'Henüz kullanıcı eklenmemiş'}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                shown.map((user) => (
                  <TableRow key={user.id} className={user.is_active === false ? 'opacity-50' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {user.is_super_admin && <Shield className="h-4 w-4 text-purple-500" />}
                        {user.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{user.email}</TableCell>
                    <TableCell>
                      {getRoleBadge(user)}
                      {currentUser?.is_super_admin && user.role === 'isg_expert' && (
                        <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" title="İSG uzmanı, firma onayıyla süreli olarak firma portalına firma adına erişebilir">
                          <Switch checked={!!user.firm_access_enabled} onCheckedChange={(v) => toggleFirmAccess(user, v)} className="scale-75 -ml-1" data-testid={`user-firm-access-toggle-${user.id}`} />
                          Firma Erişimi
                        </label>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700" data-testid={`user-company-${user.id}`}>
                      {user.company_name || <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell>
                      {user.is_super_admin || !user.allowed_locations?.length ? (
                        <span className="text-slate-400 text-sm">Tümü</span>
                      ) : (
                        <span className="text-sm">
                          {user.allowed_locations.length} lokasyon
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={user.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                        {user.is_active !== false ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit('users') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenDialog(user)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Düzenle
                          </Button>
                        )}
                        {hasPermission('users', 'reset_password') && !user.is_super_admin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedUserForPassword(user);
                              setPasswordDialogOpen(true);
                            }}
                            className="text-amber-600 border-amber-200 hover:bg-amber-50"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete('users') && !user.is_super_admin && user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedUserForDelete(user);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <StorePagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} unit="kullanıcı" prefix="users" sizes={[10, 20, 50, 100]} />
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">Genel Bilgiler</TabsTrigger>
                <TabsTrigger value="permissions">Yetkiler</TabsTrigger>
                <TabsTrigger value="locations">Lokasyonlar</TabsTrigger>
              </TabsList>
              
              {/* Genel Bilgiler */}
              <TabsContent value="general" className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Soyad</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Kullanıcı adı"
                    required
                  />
                </div>
                
                {!editingUser && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-posta</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="ornek@email.com"
                        required
                        data-testid="user-email-input"
                      />
                      <EmailCheckHint state={userEmailCheck} testId="user-email-check" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Şifre</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="En az 8 karakter, harf ve rakam"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <PasswordRules password={formData.password} testId="user-create-password-rules" />
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label>Rol</Label>
                  <Select
                    value={formData.role}
                    onValueChange={handleRoleChange}
                    disabled={editingUser?.is_super_admin || formData.role === 'firma_yetkilisi'}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue placeholder="Rol seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABELS).filter(([key]) => key !== 'magaza_yetkilisi' && (key !== 'firma_yetkilisi' || formData.role === key)).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{key === 'firma_yetkilisi' && editingUser?.company_type === 'magaza' ? ROLE_LABELS.magaza_yetkilisi.label : label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.role === 'firma_yetkilisi' && <p className="text-xs text-slate-500" data-testid="firm-role-hint">Firma kullanıcısının rolü değiştirilemez; personel ve ekipman ekle/düzenle/sil ayarlarını Yetkiler sekmesinden yönetebilirsiniz. Firma yalnız kendi firmasının kayıtlarında işlem yapar.</p>}
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    disabled={editingUser?.is_super_admin}
                  />
                  <Label htmlFor="is_active" className="font-normal">Hesap Aktif</Label>
                </div>
              </TabsContent>
              
              {/* Yetkiler */}
              <TabsContent value="permissions" className="py-4">
                <PermissionsEditor permissions={formData.permissions} disabled={editingUser?.is_super_admin} onChange={handlePermissionChange} />
              </TabsContent>
              
              {/* Lokasyonlar */}
              <TabsContent value="locations" className="py-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Kullanıcının erişebileceği lokasyonları seçin. Hiçbiri seçilmezse tüm lokasyonlara erişebilir.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllLocations}
                    >
                      {(formData.allowed_locations || []).length === locations.length ? 'Hiçbirini Seçme' : 'Tümünü Seç'}
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {locations.map((location) => {
                      const isSelected = (formData.allowed_locations || []).includes(location.id);
                      return (
                        <div
                          key={location.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-300 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleLocationToggle(location.id)}
                        >
                          <div className="flex items-center justify-center w-5 h-5">
                            {isSelected ? (
                              <div className="w-4 h-4 bg-blue-600 rounded flex items-center justify-center">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            ) : (
                              <div className="w-4 h-4 border-2 border-slate-300 rounded" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-slate-400" />
                            <span>{location.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {(formData.allowed_locations || []).length === 0 && (
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                      ⚠️ Hiçbir lokasyon seçilmedi. Bu kullanıcı tüm lokasyonlara erişebilecek.
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={userEmailCheck.status === 'taken'} className="bg-slate-900 hover:bg-slate-800" data-testid="save-user-btn">
                {editingUser ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Şifre Sıfırla</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-500">
              <strong>{selectedUserForPassword?.name}</strong> kullanıcısı için yeni şifre belirleyin.
            </p>
            <div className="space-y-2">
              <Label htmlFor="new_password">Yeni Şifre</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="En az 8 karakter, harf ve rakam"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordRules password={newPassword} testId="user-reset-password-rules" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setPasswordDialogOpen(false);
              setNewPassword('');
            }}>
              İptal
            </Button>
            <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700">
              <Key className="h-4 w-4 mr-2" />
              Şifreyi Sıfırla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{selectedUserForDelete?.name}</strong> kullanıcısını silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersPage;
